import React, { useEffect, useMemo } from 'react';
import FlatBackground from './FlatBackground';
import VideoEquirectangular from './VideoEquirectangular';
import PropTypes from 'prop-types';
import _3DScene from './_3DScene';
import BackgroundCube from './BackgroundCube';
import {
	getTextureKeys,
	loadCubemapTextures,
	loadHDRImage,
} from '@3jss/utils/textureHelpers';
import * as THREE from 'three';

/**
 * @category Background
 * @module Background
 *
 * @param scene
 * @param bgConf
 * @param resetBGBeforeImageLoaded
 * @param camera
 * @param renderer
 * @param enablePan
 * @param type
 * @param controller
 * @param transition
 * @param onBackgroundReady
 * @param onBackgroundLoaded
 * @param onBackgroundError
 * @param enableZoomControls
 * @param sceneId
 * @param avatarUrl
 * @param isXREnabled
 * @return {JSX.Element}
 */
const Background = ({
	scene,
	bgConf,
	resetBGBeforeImageLoaded,
	camera,
	renderer,
	enablePan = false,
	type,
	controller,
	transition,
	onBackgroundReady = () => {},
	onBackgroundLoaded = () => {},
	onBackgroundError = () => {},
	enableZoomControls,
	sceneId,
	avatarUrl,
	isXREnabled,
}) => {
	const sceneConfig = useMemo(
		() => ({
			sceneType: bgConf.sceneType,
			backgroundUrl: bgConf.backgroundUrl,
			environmentMapURL: bgConf.environmentMapURL,
			useSceneConfigAsEnvMap:
				bgConf?.use_scene_background_as_environment_map,
			_3model: bgConf._3model,
			opacityMapUrl: bgConf?.opacityMapUrl,
			imageIntegrity: bgConf?.imageIntegrity,
			useWebp: bgConf?.useWebp,
			skipLargest: bgConf?.skipLargest,
			enableZoomControls: enableZoomControls,
			videoBackground: bgConf.videoBackground,
			videoBackgroundOptions: bgConf.videoBackgroundOptions,
		}),
		[bgConf],
	);

	const setEnvironmentMap = (texture) => {
		scene.environment = texture;
		scene.environmentRotation = new THREE.Euler(0, Math.PI, 0, 'XYZ');
		scene.environmentIntensity = bgConf?.envMapIntensity || 1;
	};

	useEffect(() => {
		if (
			sceneConfig.environmentMapURL &&
			sceneConfig?.useSceneConfigAsEnvMap !== true
		) {
			if (sceneConfig.environmentMapURL.endsWith('.hdr')) {
				loadHDRImage(sceneConfig.environmentMapURL, renderer).then(
					(texture) => {
						setEnvironmentMap(texture);
					},
				);
			} else {
				const textureKeys = getTextureKeys(
					'512',
					sceneConfig.imageIntegrity,
					true,
				);
				loadCubemapTextures(
					sceneConfig.environmentMapURL,
					textureKeys,
					'bitmap',
				)
					.then((texture) => {
						texture.colorSpace = THREE.SRGBColorSpace;
						setEnvironmentMap(texture);
					})
					.catch((e) => console.error(e));
			}
		}
		return () => {
			scene?.environment?.dispose();
			scene.environment = null;
		};
	}, [sceneConfig.environmentMapURL, sceneConfig?.useSceneConfigAsEnvMap]);

	const loadFlatBackground = () => {
		if (type === 'zoom' && sceneConfig.enableZoomControls) {
			onBackgroundReady();
			onBackgroundLoaded();
			return <></>;
		} else {
			return (
				<FlatBackground
					backgroundUrl={sceneConfig.backgroundUrl}
					scene={scene}
					resetBGBeforeImageLoaded={resetBGBeforeImageLoaded}
					imageIntegrity={sceneConfig.imageIntegrity}
					enablePan={enablePan}
					type={type}
					useSceneBackgroundAsEnvMap={
						sceneConfig?.useSceneConfigAsEnvMap
					}
					onBackgroundReady={onBackgroundReady}
					onBackgroundLoaded={onBackgroundLoaded}
				/>
			);
		}
	};

	return (
		<>
			{bgConf.sceneType === 'flat_scene' && loadFlatBackground()}

			{bgConf.sceneType === 'cubemap_scene' && (
				<BackgroundCube
					scene={scene}
					renderer={renderer}
					backgroundUrl={sceneConfig.backgroundUrl}
					transition={transition}
					imageIntegrity={sceneConfig.imageIntegrity}
					useWebp={sceneConfig.useWebp}
					onBackgroundReady={onBackgroundReady}
					onBackgroundLoaded={onBackgroundLoaded}
					sceneId={sceneId}
					useSceneBackgroundAsEnvMap={
						sceneConfig?.useSceneConfigAsEnvMap
					}
					opacityMapUrl={sceneConfig.opacityMapUrl}
					camera={camera}
					skipLargest={sceneConfig.skipLargest}
					controller={controller}
					materialProperties={bgConf?.materialProperties}
				/>
			)}

			{['3d_model_scene', '3d_scene'].includes(bgConf?.sceneType) && (
				<_3DScene
					scene={scene}
					renderer={renderer}
					sceneType={bgConf.sceneType}
					backgroundUrl={sceneConfig.backgroundUrl}
					environmentMapURL={sceneConfig.environmentMapURL}
					envMapIntensity={bgConf?.envMapIntensity}
					transition={transition}
					imageIntegrity={sceneConfig.imageIntegrity}
					useWebp={sceneConfig.useWebp}
					onBackgroundReady={onBackgroundReady}
					onBackgroundLoaded={onBackgroundLoaded}
					sceneId={sceneId}
					useSceneBackgroundAsEnvMap={
						sceneConfig?.useSceneConfigAsEnvMap
					}
				/>
			)}

			{bgConf.sceneType === 'video_equirectangular' && (
				<VideoEquirectangular
					scene={scene}
					onBackgroundReady={onBackgroundReady}
					onBackgroundLoaded={onBackgroundLoaded}
					backgroundUrl={sceneConfig.videoBackground}
					videoBackgroundOptions={sceneConfig.videoBackgroundOptions}
					onBackgroundError={onBackgroundError}
					isXREnabled={isXREnabled}
				/>
			)}
		</>
	);
};

Background.propTypes = {
	enablePan: PropTypes.bool,
	onBackgroundReady: PropTypes.func,
	onBackgroundLoaded: PropTypes.func,
	onBackgroundError: PropTypes.func,
	isXREnabled: PropTypes.bool,
};

export default Background;
